/**
 * User Management API Client
 *
 * API functions for user, role, session, and access management.
 */

import { api } from './api'

// ============================================================================
// Types
// ============================================================================

/**
 * User from Keycloak (primary user type).
 * All user management is done through Keycloak.
 */
export interface User {
  id: string  // Keycloak UUID
  email: string
  fullName: string
  firstName: string
  lastName: string
  isActive: boolean  // Maps to 'enabled' in Keycloak
  emailVerified: boolean
  roles: string[]  // Keycloak realm roles (e.g., "system_admin", "data_manager")
  createdAt: string | null  // From PostgreSQL reference
  lastLogin: string | null  // From PostgreSQL reference
}

export interface UserBrief {
  id: string
  email: string
  fullName: string
}

export interface Role {
  id: number
  name: string
  description: string | null
  isSystemRole: boolean
  createdAt: string
  maxConcurrentSessions?: number
}

export interface UserSession {
  id: string
  userId: string
  ipAddress: string | null
  userAgent: string | null
  createdAt: string
  expiresAt: string
  lastActivity: string | null
  isActive: boolean
  isCurrent?: boolean
}

export interface StudyAccess {
  studyId: string
  studyName: string
  grantedAt: string
  grantedBy: string | null
}

export interface NavigationPage {
  id: number
  name: string
  slug: string
  parentId: number | null
  icon: string | null
  sortOrder: number
  isActive: boolean
  fullPath?: string
}

export interface PageAccess {
  pageId: number
  pageName: string
  pageSlug: string
}

export interface IPWhitelistEntry {
  id: number
  ipAddress: string
  cidrNotation: string | null
  description: string | null
  isActive: boolean
  createdAt: string
  createdBy: string | null
}

export interface MFAStatusReport {
  totalUsers: number
  mfaEnabled: number
  mfaRequired: number
  mfaPending: number
  adoptionRate: number
}

export interface IPWhitelistStatus {
  isEnabled: boolean
  totalEntries: number
  activeEntries: number
  message: string
}

export interface LockoutStatus {
  userId: string
  email: string
  isLocked: boolean
  lockedUntil: string | null
  failedLoginAttempts: number
}

export interface SyncIssue {
  type: 'orphan_pg' | 'stale_keycloak_id' | 'email_mismatch'
  severity: 'warning' | 'error'
  pgUserId: string | null
  pgEmail: string | null
  keycloakId: string | null
  keycloakEmail: string | null
  description: string
}

export interface SyncStatus {
  synced: boolean
  totalPgUsers: number
  totalKeycloakUsers: number
  issues: SyncIssue[]
  checkedAt: string
}

// Request/Response types
export interface UserListParams {
  page?: number
  pageSize?: number
  search?: string
  isActive?: boolean
  roleId?: number
}

export interface UserListResponse {
  data: User[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

export interface CreateUserPayload {
  email: string
  fullName: string
  password?: string  // Initial password (optional - user can reset via SSO)
  roles: string[]    // Keycloak realm role names (e.g., ["data_manager"])
  studyIds?: string[]  // Studies to grant access to (optional)
}

export interface UpdateUserPayload {
  email?: string
  fullName?: string
  isActive?: boolean
  roles?: string[]  // Keycloak realm role names
}

export interface CreateRolePayload {
  name: string
  description?: string
  maxConcurrentSessions?: number
}

export interface UpdateRolePayload {
  name?: string
  description?: string
  maxConcurrentSessions?: number
}

export interface IPWhitelistPayload {
  ipAddress: string
  cidrNotation?: string
  description?: string
  isActive?: boolean
}

// ============================================================================
// User Management
// ============================================================================

/**
 * Fetch paginated list of users with optional filters
 */
export async function fetchUsers(params: UserListParams = {}): Promise<UserListResponse> {
  const searchParams = new URLSearchParams()
  if (params.page) searchParams.set('page', params.page.toString())
  if (params.pageSize) searchParams.set('page_size', params.pageSize.toString())
  if (params.search) searchParams.set('search', params.search)
  if (params.isActive !== undefined) searchParams.set('is_active', params.isActive.toString())

  const queryString = searchParams.toString()
  const url = queryString ? `/users?${queryString}` : '/users'

  const response = await api.get<UserListResponse>(url)
  return response.data
}

/**
 * Fetch a single user by ID
 */
export async function fetchUserById(userId: string): Promise<User> {
  const response = await api.get<User>(`/users/${userId}`)
  return response.data
}

/**
 * Create a new user in Keycloak
 */
export async function createUser(data: CreateUserPayload): Promise<User> {
  const payload = {
    email: data.email,
    full_name: data.fullName,
    password: data.password,
    roles: data.roles,
  }
  const response = await api.post<User>('/users', payload)
  return response.data
}

/**
 * Update an existing user in Keycloak
 */
export async function updateUser(userId: string, data: UpdateUserPayload): Promise<User> {
  const payload: Record<string, unknown> = {}
  if (data.email !== undefined) payload.email = data.email
  if (data.fullName !== undefined) payload.full_name = data.fullName
  if (data.isActive !== undefined) payload.is_active = data.isActive
  if (data.roles !== undefined) payload.roles = data.roles

  const response = await api.put<User>(`/users/${userId}`, payload)
  return response.data
}

/**
 * Deactivate (soft delete) a user
 */
export async function deleteUser(userId: string): Promise<void> {
  await api.delete(`/users/${userId}`)
}

/**
 * Unlock a locked user account
 */
export async function unlockUser(userId: string): Promise<LockoutStatus> {
  const response = await api.post<LockoutStatus>(`/users/${userId}/unlock`)
  return response.data
}

/**
 * Get user's lockout status
 */
export async function getUserLockoutStatus(userId: string): Promise<LockoutStatus> {
  const response = await api.get<LockoutStatus>(`/users/${userId}/lockout-status`)
  return response.data
}

// ============================================================================
// User-Study Access
// ============================================================================

/**
 * Fetch studies a user has access to
 */
export async function fetchUserStudies(userId: string): Promise<StudyAccess[]> {
  const response = await api.get<StudyAccess[]>(`/users/${userId}/studies`)
  return response.data
}

/**
 * Grant study access to a user
 */
export async function grantUserStudyAccess(userId: string, studyIds: string[]): Promise<{ granted: number }> {
  const response = await api.post<{ granted: number }>(`/users/${userId}/studies`, {
    study_ids: studyIds,
  })
  return response.data
}

/**
 * Revoke study access from a user
 */
export async function revokeUserStudyAccess(userId: string, studyId: string): Promise<void> {
  await api.delete(`/users/${userId}/studies/${studyId}`)
}

// ============================================================================
// Session Management
// ============================================================================

/**
 * Fetch active sessions for a user
 */
export async function fetchUserSessions(userId: string): Promise<UserSession[]> {
  const response = await api.get<UserSession[]>(`/users/${userId}/sessions`)
  return response.data
}

/**
 * Terminate a specific session
 */
export async function terminateSession(userId: string, sessionId: string): Promise<void> {
  await api.delete(`/users/${userId}/sessions/${sessionId}`)
}

/**
 * Terminate all sessions for a user (except current)
 */
export async function terminateAllSessions(userId: string): Promise<{ terminated: number }> {
  const response = await api.delete<{ terminated: number }>(`/users/${userId}/sessions`)
  return response.data
}

// ============================================================================
// MFA Management
// ============================================================================

/**
 * Get MFA status for a specific user (admin)
 */
export async function getUserMfaStatus(userId: string): Promise<{
  userId: string
  email: string
  fullName: string
  mfaEnabled: boolean
  methods: string[]
  enrolledAt: string | null
}> {
  const response = await api.get(`/users/${userId}/mfa/status`)
  return response.data
}

/**
 * Reset MFA for a user (admin action)
 * Use when user has lost their phone or authenticator app
 */
export async function resetUserMfa(userId: string): Promise<{
  success: boolean
  message: string
  removedCount: number
}> {
  const response = await api.post(`/users/${userId}/mfa/reset`)
  return response.data
}

/**
 * Require MFA for a user
 */
export async function requireUserMFA(userId: string): Promise<void> {
  await api.post(`/users/${userId}/mfa/require`)
}

/**
 * Remove MFA requirement from a user
 */
export async function removeUserMFARequirement(userId: string): Promise<void> {
  await api.delete(`/users/${userId}/mfa/require`)
}

/**
 * Get MFA adoption status report
 */
export async function fetchMFAStatus(): Promise<MFAStatusReport> {
  const response = await api.get<MFAStatusReport>('/users/mfa-status')
  return response.data
}

// ============================================================================
// Role Management (Keycloak)
// ============================================================================

/**
 * Fetch available Keycloak realm roles
 */
export async function fetchAvailableRoles(): Promise<string[]> {
  const response = await api.get<string[]>('/users/roles/available')
  return response.data
}

/**
 * Fetch all roles (legacy - for backwards compatibility)
 * @deprecated Use fetchAvailableRoles() instead
 */
export async function fetchRoles(): Promise<Role[]> {
  const response = await api.get<{ data: Role[]; total: number }>('/roles')
  return response.data.data
}

/**
 * Fetch a single role by ID
 */
export async function fetchRoleById(roleId: number): Promise<Role> {
  const response = await api.get<Role>(`/roles/${roleId}`)
  return response.data
}

/**
 * Create a new custom role
 */
export async function createRole(data: CreateRolePayload): Promise<Role> {
  const response = await api.post<Role>('/roles', {
    name: data.name,
    description: data.description,
    max_concurrent_sessions: data.maxConcurrentSessions,
  })
  return response.data
}

/**
 * Update a role
 */
export async function updateRole(roleId: number, data: UpdateRolePayload): Promise<Role> {
  const payload: Record<string, unknown> = {}
  if (data.name !== undefined) payload.name = data.name
  if (data.description !== undefined) payload.description = data.description
  if (data.maxConcurrentSessions !== undefined) payload.max_concurrent_sessions = data.maxConcurrentSessions

  const response = await api.put<Role>(`/roles/${roleId}`, payload)
  return response.data
}

/**
 * Delete a custom role
 */
export async function deleteRole(roleId: number): Promise<void> {
  await api.delete(`/roles/${roleId}`)
}

// ============================================================================
// Role-Page Access
// ============================================================================

/**
 * Fetch all navigation pages
 */
export async function fetchAllPages(): Promise<NavigationPage[]> {
  const response = await api.get<NavigationPage[]>('/pages')
  return response.data
}

/**
 * Fetch pages accessible by a role
 */
export async function fetchRolePages(roleId: number): Promise<PageAccess[]> {
  const response = await api.get<PageAccess[]>(`/roles/${roleId}/pages`)
  return response.data
}

/**
 * Grant page access to a role
 */
export async function grantRolePageAccess(roleId: number, pageIds: number[]): Promise<{ granted: number }> {
  const response = await api.post<{ granted: number }>(`/roles/${roleId}/pages`, {
    page_ids: pageIds,
  })
  return response.data
}

/**
 * Revoke page access from a role
 */
export async function revokeRolePageAccess(roleId: number, pageId: number): Promise<void> {
  await api.delete(`/roles/${roleId}/pages/${pageId}`)
}

// ============================================================================
// IP Whitelist Management
// ============================================================================

/**
 * Fetch IP whitelist entries
 */
export async function fetchIPWhitelist(): Promise<IPWhitelistEntry[]> {
  const response = await api.get<IPWhitelistEntry[]>('/admin/ip-whitelist')
  return response.data
}

/**
 * Add an IP to the whitelist
 */
export async function addIPToWhitelist(data: IPWhitelistPayload): Promise<IPWhitelistEntry> {
  const response = await api.post<IPWhitelistEntry>('/admin/ip-whitelist', {
    ip_address: data.ipAddress,
    cidr_notation: data.cidrNotation,
    description: data.description,
    is_active: data.isActive ?? true,
  })
  return response.data
}

/**
 * Update an IP whitelist entry
 */
export async function updateIPWhitelist(id: number, data: IPWhitelistPayload): Promise<IPWhitelistEntry> {
  const response = await api.put<IPWhitelistEntry>(`/admin/ip-whitelist/${id}`, {
    ip_address: data.ipAddress,
    cidr_notation: data.cidrNotation,
    description: data.description,
    is_active: data.isActive,
  })
  return response.data
}

/**
 * Remove an IP from the whitelist
 */
export async function removeIPFromWhitelist(id: number): Promise<void> {
  await api.delete(`/admin/ip-whitelist/${id}`)
}

/**
 * Check if an IP is allowed
 */
export async function checkIPAllowed(ip: string): Promise<{ allowed: boolean; reason?: string }> {
  const response = await api.post<{ allowed: boolean; reason?: string }>('/admin/ip-whitelist/check', {
    ip_address: ip,
  })
  return response.data
}

/**
 * Get IP whitelist enforcement status
 */
export async function fetchIPWhitelistStatus(): Promise<IPWhitelistStatus> {
  const response = await api.get<IPWhitelistStatus>('/admin/ip-whitelist/status')
  return response.data
}

// ============================================================================
// Accessible Pages (for current user)
// ============================================================================

export interface AccessiblePage {
  id: number
  name: string
  slug: string
  icon: string | null
  parentId: number | null
  sortOrder: number
}

/**
 * Fetch pages the current user can access
 */
export async function fetchMyAccessiblePages(): Promise<AccessiblePage[]> {
  const response = await api.get<AccessiblePage[]>('/auth/me/pages')
  return response.data
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get display name for a role
 */
export function getRoleDisplayName(roleName: string): string {
  const displayNames: Record<string, string> = {
    system_admin: 'System Administrator',
    data_manager: 'Data Manager',
    statistical_analyst: 'Statistical Analyst',
    clinical_reviewer: 'Clinical Reviewer',
  }
  return displayNames[roleName] || roleName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

/**
 * Get badge variant for user status
 */
export function getUserStatusVariant(user: User): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (!user.isActive) {
    return 'secondary'
  }
  if (!user.emailVerified) {
    return 'outline'
  }
  return 'default'
}

/**
 * Get status text for user
 */
export function getUserStatusText(user: User): string {
  if (!user.isActive) {
    return 'Disabled'
  }
  if (!user.emailVerified) {
    return 'Pending Verification'
  }
  return 'Active'
}

// ============================================================================
// Keycloak-PostgreSQL Sync Status
// ============================================================================

/**
 * Check sync status between Keycloak and PostgreSQL
 */
export async function fetchSyncStatus(): Promise<SyncStatus> {
  // API interceptor already converts snake_case to camelCase
  const response = await api.get<SyncStatus>('/users/admin/sync-status')
  return response.data
}

/**
 * Fix a sync issue between Keycloak and PostgreSQL
 */
export async function fixSyncIssue(
  issueType: SyncIssue['type'],
  pgUserId: string
): Promise<{ success: boolean; action: string }> {
  const response = await api.post<{ success: boolean; action: string }>(
    '/users/admin/sync-status/fix',
    {
      issue_type: issueType,
      pg_user_id: pgUserId,
    }
  )
  return response.data
}
